<template>
	<div
		v-if="printLoading"
		class="modal justify-center flex text-center items-center">
		<tw-loading />
		<div
			v-if="printLoading"
			class="absolute top-0 right-0 w-screen h-screen flex justify-center items-center pt-16 font-bold font-gotham text-brand-500">
			<div v-if="printingReport">
				{{ t("job.creatingReport") }}
			</div>
			<div v-else-if="exportingSongs">
				{{ t("job.exportingSongs") }}
			</div>
			<div v-else>
				{{ t("job.creatingInstructions") }}
			</div>
		</div>
	</div>
	<div>
		<div class="flex justify-between items-center pb-3 mb-8 border-b-2 border-brand-400">
			<tw-breadcrumb 
				:crumbs="breadcrumb" />
		</div>
		<div class="block md:flex justify-between items-center w-full gap-4">
			<div
				v-show="loading"
				class="h-6 rounded-full bg-gray-300 animate-pulse w-1/2  mb-8" />
			<h1
				v-show="!loading"
				class="page-title"
				:class="{'text-indigo-500' : job?.fieldData?.['job__JOBS__kfJobsID::_kp_jobs_id']}">
				{{ job?.fieldData?.name_bride_groom_display }}
			</h1>
			<div class="block md:flex gap-x-6 pb-3 md:pb-0 space-y-3 md:space-y-0">
				<div 
					v-if="canAccessSongExport && isWeddingCategory"
					@click="exportSceneSongs()"
					class="flex justify-end gap-2 items-center linked-icon cursor-pointer">
					<img
						class="h-6 w-6"
						src="@/assets/icons/solid/upload-fill.svg"
						alt="printer icon">
					<span>{{ t("job.exportSongs") }}</span>
				</div>
				<div
					v-if="userStore.getContactType == 'Staff'"
					@click="printInstructions()" 
					class="flex justify-end gap-2 items-center linked-icon cursor-pointer">
					<img
						class="h-6 w-6"
						src="@/assets/icons/solid/print-fill.svg"
						alt="printer icon">
					<span>{{ t("job.printInstructions") }}</span>
				</div>
				<div 
					v-if="canAccessServiceSequenceOrderReport"
					@click="printServiceSequenceOrderReport()"
					class="flex justify-end gap-2 items-center linked-icon cursor-pointer">
					<img
						class="h-6 w-6"
						src="@/assets/icons/solid/print-fill.svg"
						alt="printer icon">
					<span>{{ t("job.printServiceSequenceOrderReport") }}</span>
				</div>	
			</div>		
		</div>
		<div class="grid grid-cols-2 gap-4 lg:gap-6">
			<!-- job details & locations -->
			<div :class="[userStore.getContactType === 'Staff' || job.fieldData?.zcnIsParty ? 'col-span-2' : 'col-span-2 lg:col-span-1']">
				<collapsable-card 
					:title="t('job.jobDetails')">
					<JobDetailsLocation 
						:job="job"
						:loading="loading" />
				</collapsable-card>
			</div>

			<!-- covid restrictions -->
			<div
				v-if="userStore.getContactType != 'Staff' && !job.fieldData?.zcnIsParty"
				class="col-span-2 lg:col-span-1">
				<collapsable-card
					:title="_localesStore.getLabel('278')">
					<JobCovidRestrictions
						:job="job"
						:loading="loading" />
				</collapsable-card>
			</div>

			<!-- transportation -->
			<div 
				v-if="userStore.getContactType === 'Staff'"
				class="col-span-2 lg:col-span-1">
				<collapsable-card 
					:title="_localesStore.getLabel('185')">
					<JobTransportationDetails
						:job="job"
						:loading="loading" />
				</collapsable-card>
			</div>

			<!-- meeeting places -->
			<div 
				v-if="userStore.getContactType === 'Staff'"
				class="col-span-2 lg:col-span-1">
				<collapsable-card 
					:title="_localesStore.getLabel('427')">
					<JobMeetingPlaces
						:job="job"
						:loading="loading" />
				</collapsable-card>
			</div>

			<!-- locations -->
			<div 
				v-if="userStore.getContactType === 'Staff'"
				class="col-span-2 lg:col-span-1">
				<collapsable-card 
					:title="_localesStore.getLabel('218')">
					<JobLocations
						:job="job"
						:loading="loading" />
				</collapsable-card>
			</div>

			<!-- items -->
			<div
				v-if="userStore.getContactType === 'Staff'" 
				class="col-span-2 lg:col-span-1">
				<collapsable-card 
					:title="_localesStore.getLabel('187')">
					<JobItems 
						:job="job"
						:loading="loading" />
				</collapsable-card>
			</div>
				
			<!-- additional details -->
			<div class="col-span-2">
				<collapsable-card 
					:title="t('job.additionalDetails')">
					<JobAdditionalDetails 
						:job="job"
						:loading="loading" />
				</collapsable-card>
			</div>

			<!-- discounts -->
			<div
				v-if="userStore.getContactType != 'Staff'"
				:class="[job.fieldData?.zcnIsParty ? 'col-span-2' : 'col-span-2 lg:col-span-1']">
				<collapsable-card 
					:title="_localesStore.getLabel('153')">
					<JobDiscounts 
						:job="job"
						:loading="loading" />
				</collapsable-card>
			</div>

			<!-- staff notes -->
			<div
				v-if="userStore.getContactType == 'Staff'"
				class="col-span-2 lg:col-span-1">
				<collapsable-card 
					:title="_localesStore.getLabel('205')">
					<div
						class="w-full px-4">
						<!-- notes -->
						<data-display
							:loading="loading"
							:value="job?.fieldData?.['job__JOBS_INFO__jobID::notes_jobs_staff_display'] || '—'" />
					</div>
				</collapsable-card>
			</div>
			
			<!-- introductions -->
			<div
				v-if="!job.fieldData?.zcnIsParty" 
				class="col-span-2 lg:col-span-1">
				<collapsable-card 
					:title="_localesStore.getLabel('347')">
					<JobIntroductions 
						:job="job"
						:loading="loading" />
				</collapsable-card>
			</div>

			<!-- staff participants -->
			<div
				v-if="userStore.getContactType == 'Staff'"
				class="col-span-2">
				<collapsable-card 
					:title="_localesStore.getLabel('156')">
					<JobParticipants 
						:job="job"
						:loading="loading" />
				</collapsable-card>
			</div>

			<!-- items -->
			<div
				v-if="userStore.getContactType != 'Staff'"
				:class="['col-span-2']">
				<collapsable-card 
					:title="_localesStore.getLabel('187')">
					<JobItems 
						:job="job"
						:loading="loading" />
				</collapsable-card>
			</div>

			<!-- staff options -->
			<div
				v-if="userStore.getContactType == 'Staff'"
				class="col-span-2">
				<collapsable-card 
					:title="_localesStore.getLabel('175')">
					<JobOptions 
						:job="job"
						:loading="loading" />
				</collapsable-card>
			</div>

			<!-- roles -->
			<div class="col-span-2">
				<collapsable-card 
					:title="_localesStore.getLabel('179')">
					<JobRoles 
						:job="job"
						:loading="loading" />
				</collapsable-card>
			</div>

			<!-- participants -->
			<div
				v-if="userStore.getContactType != 'Staff'"
				class="col-span-2">
				<collapsable-card 
					:title="_localesStore.getLabel('156')">
					<JobParticipants 
						:job="job"
						:loading="loading" />
				</collapsable-card>
			</div>
	
			<!-- options -->
			<div
				v-if="userStore.getContactType != 'Staff'"
				class="col-span-2">
				<collapsable-card 
					:title="_localesStore.getLabel('175')">
					<JobOptions 
						:job="job"
						:loading="loading" />
				</collapsable-card>
			</div>
			
			<!-- variations -->
			<div class="col-span-2">
				<collapsable-card 
					:title="_localesStore.getLabel('394')">
					<JobVariations 
						:job="job"
						:loading="loading" />
				</collapsable-card>
			</div>

			<!-- scenes -->
			<div 
				class="col-span-2"
				id="scenes">
				<collapsable-card 
					:title="_localesStore.getLabel('178')"
					no-fill>
					<div 
						v-if="isClient && isWeddingCategory && scenesAreEditable && canAccessSceneEditor"
						@click="openSceneEditor"
						class="flex justify-end gap-2 items-center linked-icon cursor-pointer absolute -mt-10 ml-24">
						<div>
							<PencilIconMini
								class="size-4" />
						</div>
						<div>
							{{ t("sceneEditor.edit") }}
						</div>
					</div>
					<div class="col-span-2 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
						<div
							v-for="scene in job?.portalData?.portal_scenes"
							:key="scene.recordId"
							class="col-span-1">
							<SceneCard
								:job-name="job?.fieldData?.name_bride_groom_display"
								:scene="scene" />
						</div>
					</div>
				</collapsable-card>
			</div>			

			<!-- borrowed items -->
			<div class="col-span-2">
				<collapsable-card 
					:title="_localesStore.getLabel('219')">
					<JobBorrowedItems 
						:job="job"
						:loading="loading" />
				</collapsable-card>
			</div>

			<!-- documents -->
			<div class="col-span-2">
				<collapsable-card 
					:title="_localesStore.getLabel('203')"
					no-fill>
					<!-- Client Documents -->
					<div	
						v-if="isClient"				
						class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
						<a
							v-for="(document, i) in job?.portalData?.portal_documents_client"
							:key="i"
							class="w-full truncate hover:opacity-70"
							:href="document.signedUrl"
							download>
							<div
								v-if="document.ext == 'jpeg' || document.ext == 'jpg' || document.ext == 'png' || document.ext == 'gif'"
								class="flex bg-gray-100 border-2 border-gray-200  w-full h-48 m-auto text-brand-500 rounded-md">
								<img
									:src="document.signedUrl"
									class="w-full h-full object-cover rounded-md">
							</div>
							<div
								v-else
								class="flex bg-gray-100 border-2 border-gray-200  w-full h-48 m-auto text-brand-500 rounded-md">
								<img
									class="linked-icon w-16 m-auto"
									src="@/assets/icons/solid/document-fill.svg"
									alt="document icon">
							</div>
							<div class="text-gray-900 font-medium mt-2">
								{{ document['job__DOCUMENTS__jobID::name_display'] }} 
							</div>
							<div class="text-sm">
								(v{{ document['job__DOCUMENTS__jobID::version'] }})
							</div>
							<div class="text-sm">
								{{ document['job__DOCUMENTS__jobID::description_display'] }}
							</div>
						</a>
					</div>
					<!-- Staff Documents -->
					<div		
						v-else			
						class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
						<a
							v-for="(document, i) in job?.portalData?.portal_documents_staff"
							:key="i"
							class="w-full truncate hover:opacity-70"
							:href="document.signedUrl"
							download>
							<div
								v-if="document.ext == 'jpeg' || document.ext == 'jpg' || document.ext == 'png' || document.ext == 'gif'"
								class="flex bg-gray-100 border-2 border-gray-200  w-full h-48 m-auto text-brand-500 rounded-md">
								<img
									:src="document.signedUrl"
									class="w-full h-full object-cover rounded-md">
							</div>
							<div
								v-else
								class="flex bg-gray-100 border-2 border-gray-200  w-full h-48 m-auto text-brand-500 rounded-md">
								<img
									class="linked-icon w-16 m-auto"
									src="@/assets/icons/solid/document-fill.svg"
									alt="document icon">
							</div>
							<div class="text-gray-900 font-medium mt-2">
								{{ document['job__DOCUMENTS__jobID::name_display'] }}
							</div>
							<div class="text-sm">
								(v{{ document['job__DOCUMENTS__jobID::version'] }})
							</div>
							<div class="text-sm">
								{{ document['job__DOCUMENTS__jobID::description_display'] }}
							</div>
						</a>
						<a
							v-for="(document, i) in job?.portalData?.portal_documents_staff_role"
							:key="i"
							class="w-full truncate hover:opacity-70"
							:href="document.signedUrl"
							download>
							<div
								v-if="document.ext == 'jpeg' || document.ext == 'jpg' || document.ext == 'png' || document.ext == 'gif'"
								class="flex bg-gray-100 border-2 border-gray-200  w-full h-48 m-auto text-brand-500 rounded-md">
								<img
									:src="document.signedUrl"
									class="w-full h-full object-cover rounded-md">
							</div>
							<div
								v-else
								class="flex bg-gray-100 border-2 border-gray-200  w-full h-48 m-auto text-brand-500 rounded-md">
								<img
									class="linked-icon w-16 m-auto"
									src="@/assets/icons/solid/document-fill.svg"
									alt="document icon">
							</div>
							<div class="text-gray-900 font-medium mt-2">
								{{ document['job__DOCUMENTS__gContactID::name_display'] }} <span class="text-gray-500">(v{{ document['job__DOCUMENTS__gContactID::version'] }})</span>
							</div>
							<div class="text-sm">
								{{ document['job__DOCUMENTS__gContactID::description_display'] }}
							</div>
						</a>
					</div>
				</collapsable-card>
			</div>
	
			<div
				v-if="job?.portalData?.portal_meetings.length"
				class="col-span-2">
				<collapsable-card 
					:title="_localesStore.getLabel('412')"
					no-fill>
					<Meeting
						v-for="(meeting, i) in job?.portalData?.portal_meetings"
						class="first:rounded-t last:rounded-b rounded-t-none rounded-b-none"
						:key="i"
						:meeting="meeting" />
				</collapsable-card>
			</div>
			<div
				v-if="job?.portalData?.portal_related_jobs.length"
				class="col-span-2">
				<collapsable-card 
					:title="_localesStore.getLabel('612')"
					no-fill>
					<div
						v-for="(relatedJob, i) in job?.portalData?.portal_related_jobs"
						:key="i">
						<Job
							:job="relatedJob"
							related
							hide-line />
					</div>
				</collapsable-card>
			</div>
		</div>
	</div>

	<!-- Notification -->
	<tw-notification 
		v-if="showNotification" 
		@close="showNotification = false" 
		:title="notificationTitle" 
		:subtitle="notificationSubtitle" 
		:duration="8000" 
		:icon="notificationIcon" />

	<!-- Scene Editor Modal -->
	<tw-modal
		:breadcrumb="breadcrumb"
		allow-independent-scrolling
		hide-close
		class-name="!h-screen !w-screen !max-w-none !rounded-none !overflow-y-hidden"
		ref="sceneEditorOpen"
		:title="t('sceneEditor.modalTitle')">
		<SceneEditor
			@close-modal="(command, notification) => {closeSceneEditor(command, notification)}" />
	</tw-modal>
</template>

<script lang="ts">
	export default { name: 'JobDetails' } // name the component
</script>

<script setup lang="ts">
	import { // api
		Auth
	} from '@/api/Auth'

	import JSZip, {
		folder
	} from 'jszip'

	import { 
		getJobByRecordId,
		getJobPrintInstructions,
		getServiceSequenceOrderReport
	} from '../queries'

	import JobDetailsLocation from './DetailsLocation.vue' 	// components
	import JobCovidRestrictions from './CovidRestrictions.vue'
	import JobAdditionalDetails from './AdditionalDetails.vue'
	import JobDiscounts from './Discounts.vue'
	import JobIntroductions from './Introductions.vue'
	import JobItems from './Items.vue'
	import JobRoles from './Roles.vue'
	import JobOptions from './Options.vue'
	import JobVariations from './Variations.vue'
	import JobParticipants from './Participants.vue'
	import JobBorrowedItems from './BorrowedItems.vue'
	import JobTransportationDetails from './TransportationDetails.vue'
	import JobMeetingPlaces from './MeetingPlaces.vue'
	import JobLocations from './Locations.vue'
	import SceneCard from './SceneCard.vue'
	import Meeting from './Meeting.vue'
	import Job from './Job.vue'
	import SceneEditor from './modals/sceneEditor/SceneEditor.vue'

	import EventBus from '@/events' // events

	import { // languages
		useI18n
	} from 'vue-i18n'

	import { // router
		useRouter
	} from 'vue-router'

	import { // stores
		localesStore
	} from '@/store/locales'

	import { 
		store as _userStore
	} from '@/store/user'
	import {
		useSceneEditorStore
	} from '@/store/sceneEditor'

	import { // types
		Notification
	} from '@/types'

	import { 
		FileMakerRecord
	} from '@/types'

	import { // vue
		computed,
		inject,
		onBeforeUnmount,
		onMounted,
		ref
	} from 'vue'

	const router = useRouter()

	const _localesStore = localesStore()
	const userStore = _userStore()
	const sceneEditorStore = useSceneEditorStore()

	const {t} = useI18n()

	const props = defineProps({
		recordId: {
			type: String,
			default: ''
		} 
	})

	const breadcrumb = computed(() => {
		return [
			{label: t('nav.jobsMeetings'), to: {name: 'Jobs'}},
			{label: job.value?.fieldData?.name_bride_groom_display, to: {}},
		]
	})

	const loading = ref(false)

	const job = ref<FileMakerRecord>({} as FileMakerRecord)

	const query = computed(() => {
		const contactId = userStore.user.contactId
		const isStaff = userStore.getContactType === 'Staff'
		const contactIdField = isStaff ? 'job__JOBS_ROLES__jobID::_kf_contacts_id' : '_kf_contacts_id'

		const querySearch = [
			{
				[contactIdField]: contactId,
				recordId: props.recordId
			}
		]

		return querySearch
	})

	onMounted(async () => {
		getData()

		EventBus.on('change-language', async () => {
			await getData(true)
		})
	})

	onBeforeUnmount(() => {
		EventBus.off('change-language', null)
	})
	
	const s3: any = inject('s3')
	async function getData(reset: boolean = false) {
		try {
			loading.value = true

			const contactId = userStore.user.contactId
			const sessionId = userStore.user.sessionId
			const scriptParams = JSON.stringify({
				language: _localesStore.getFmLanguage,
				sessionId,
				contactId
			})

			let params = {
				query: query.value,
				limit: 1,
				"script.prerequest": "Set Language",
				"script.prerequest.param": scriptParams,
				"limit.portal_service_scenes": 100
			}

			// fetch job by record id
			let res = await getJobByRecordId(params)
			const record = res.response.data[0]
			job.value = record

			// get documents from S3
			if(isClient.value) {
				job.value.portalData.portal_documents_client.forEach((doc: any) => {
					const s3Json = JSON.parse(doc?.['job__DOCUMENTS__jobID::s3JSONDocument'] || "{}")
					const key = s3Json?.s3FilePathShortRaw
					const ext = s3Json?.fileExt
					doc.ext = ext
					if(key) {
						let params = {
							Bucket: process.env.VUE_APP_S3_BUCKET,
							Key: key,
							ResponseContentDisposition: `attachment;`
						}

						s3.getSignedUrlPromise('getObject', params).then(
							function (url: string) {
								doc.signedUrl = url
							}, 
							function (err: Error) { 
								throw err
							}
						)
					}
				})
			}
			else {
				// Job documents
				job.value.portalData.portal_documents_staff.forEach((doc: any) => {
					const s3Json = JSON.parse(doc?.['job__DOCUMENTS__jobID::s3JSONDocument'] || "{}")
					const key = s3Json?.s3FilePathShortRaw
					const ext = s3Json?.fileExt
					doc.ext = ext
					if(key) {
						let params = {
							Bucket: process.env.VUE_APP_S3_BUCKET,
							Key: key,
							ResponseContentDisposition: `attachment;`
						}

						s3.getSignedUrlPromise('getObject', params).then(
							function (url: string) {
								doc.signedUrl = url
							}, 
							function (err: Error) { 
								throw err
							}
						)
					}
				})
				// Role documents
				job.value.portalData.portal_documents_staff_role.forEach((doc: any) => {
					const s3Json = JSON.parse(doc?.['job__DOCUMENTS__gContactID::s3JSONDocument'] || "{}")
					const key = s3Json?.s3FilePathShortRaw
					const ext = s3Json?.fileExt
					doc.ext = ext
					if(key) {
						let params = {
							Bucket: process.env.VUE_APP_S3_BUCKET,
							Key: key,
							ResponseContentDisposition: `attachment;`
						}

						s3.getSignedUrlPromise('getObject', params).then(
							function (url: string) {
								doc.signedUrl = url
							}, 
							function (err: Error) { 
								throw err
							}
						)
					}
				})
			}
			

			// Update store
			sceneEditorStore.sceneEditor.availableScenes = [...job.value.portalData.portal_service_scenes]
			sceneEditorStore.sceneEditor.scenes = [...job.value.portalData.portal_main_scenes]
			sceneEditorStore.sceneEditor.jobId = job.value.fieldData['_kp_jobs_id']
		}
		catch(e: any) {
			console.error(e)

			// redirect if server session expired 
			if(e?.code == "952") {
				await Auth.signOut()
				router.push({name: "Login"})
			}
		}
		finally {
			loading.value = false
		}
	}
	
	const printLoading = ref(false)
	
	const signedInStaffRole = computed(() => {
		if(userStore.getContactType === 'Staff') {
			const musicianRole = job.value.portalData.portal_musician_role_staff.filter((role: any) => {
				return role?.['job__JOBS_ROLES__instrumentalJobID_gStaffID::_kf_contacts_id'] === userStore.user.contactId
			})
			const liveRole = job.value.portalData.portal_live_role_staff.filter((role: any) => {
				return role?.['job__JOBS_ROLES__liveJobID_gStaffID::_kf_contacts_id'] === userStore.user.contactId
			})
			const standardRole = job.value.portalData.portal_standard_roles_staff.filter((role: any) => {
				return role?.['job__JOBS_ROLES__standardJobID_gStaffID::_kf_contacts_id'] === userStore.user.contactId
			})
			const roleName = [...musicianRole, ...liveRole, ...standardRole][0]['job__job_role__ROLES__roleID_instrumental_gStaffID::name_popup_instructions_join'].split("\r")[0]
			return roleName
		}
		else {
			return null
		}
	})

	async function printInstructions() {
		if(loading.value) return
		try {
			printLoading.value = true
			const contactId = userStore.user.contactId
			const sessionId = userStore.user.sessionId
			const scriptParams = JSON.stringify({
				language: _localesStore.getFmLanguage,
				sessionId,
				contactId,
				jobId: job.value.fieldData._kp_jobs_id,
				role: signedInStaffRole.value
			})

			let res = await getJobPrintInstructions(scriptParams)
			const pdfBase64 = JSON.parse(res.response.scriptResult).results.file

			const linkSource = `data:application/pdf;base64,${pdfBase64}`
			const downloadLink = document.createElement("a")
			const fileName = JSON.parse(res.response.scriptResult).results.fileName

			downloadLink.href = linkSource
			downloadLink.download = fileName
			downloadLink.click()

		}
		catch(e) {
			console.error(e)
		}
		finally {
			printLoading.value = false
		}
	}

	const printingReport = ref(false)

	async function printServiceSequenceOrderReport() {
		if(loading.value) return
		try {
			printLoading.value = true
			printingReport.value = true
			const scriptParams = JSON.stringify({
				language: _localesStore.getFmLanguage,
				jobId: job.value.fieldData._kp_jobs_id
			})

			let res = await getServiceSequenceOrderReport(scriptParams)
			const pdfBase64 = JSON.parse(res.response.scriptResult).file

			const linkSource = `data:application/pdf;base64,${pdfBase64}`
			const downloadLink = document.createElement("a")
			const fileName = JSON.parse(res.response.scriptResult).fileName

			downloadLink.href = linkSource
			downloadLink.download = fileName
			downloadLink.click()

		}
		catch(e) {
			console.error(e)
		}
		finally {
			printLoading.value = false
			printingReport.value = false
		}
	}

	const exportingSongs = ref(false)
	const fetchS3File: any = inject('fetchS3File')

	async function exportSceneSongs() {
		if(loading.value) return
		try {
			printLoading.value = true
			exportingSongs.value = true

			// Create download folder
			const zip = new JSZip()
			const jobDate = job.value.fieldData.zctStartDateFormatted
			const jobId = job.value.fieldData['_kp_jobs_id']
			const brideGroomName = job.value.fieldData.name_bride_groom_display
			const jobTime = job.value.fieldData.zctStartTimeFormatted
			const room = job.value.fieldData['job__ROOMS__roomID::name_full_display']
			const folderName = `${jobDate}-ID${jobId}-${brideGroomName}-${jobTime}-${room}`
			const downloadFolder = zip.folder(folderName)

			// Export all main scene songs
			await Promise.all(job.value?.portalData?.portal_main_scenes?.map(async (record: any) => {
				const songId = record['job__JOBS_SCENES__jobID::zctSongID']

				if(songId?.length) {
					// Ensure 2 digit scene number
					const sceneNumber = String(record['job__JOBS_SCENES__jobID::scene_number']).padStart(2, '0')
					const sceneName = record['job__job_scene__SCENES__sceneID::name_display']
					const songName = record['jobs__jobs_scenes__SONGS__songID::name_display']
					const songFileJSON = record['jobs__jobs_scenes__SONGS_INFO__songID::s3JSONSong']
					const fileExtension = JSON.parse(songFileJSON).fileExt
					const fileName = `${sceneNumber} ${sceneName} - ${songName}.${fileExtension}`

					// Get song from s3 and add to download folder
					const signedUrl = await fetchS3File(songFileJSON)
					const song = await fetch(signedUrl).then(r => r.blob())
					downloadFolder?.file(fileName, song)
				}
			}))
			
			// Will count the folder name as a key
			const fileLength = Object.keys(downloadFolder?.files || '{}').length - 1
			
			if(fileLength > 0) {
				// Generate zip and trigger download
				await zip.generateAsync({ type: 'blob' }).then(content => {
					const url = URL.createObjectURL(content)
					const downloadLink = document.createElement("a")
					downloadLink.href = url
					downloadLink.download = `${folderName}.zip`
					downloadLink.click()
				})
			}
		}
		catch(e) {
			console.error(e)
		}
		finally {
			printLoading.value = false
			printingReport.value = false
		}
	}

	// #region Scene Editor
	const isClient = ref(userStore.getContactType === 'Client')

	const isWeddingCategory = computed(() => {
		if(job.value?.fieldData?.zctCategoryEnglish === 'Wedding') return true
		return false
	})

	const canAccessSceneEditor = computed(() => {
		return Boolean(userStore?.user?.canAccessSceneEditor)
	})

	const canAccessServiceSequenceOrderReport = computed(() => {
		return Boolean(userStore?.user?.canAccessServiceSequenceOrderReport)
	})

	const canAccessSongExport = computed(() => {
		return Boolean(userStore?.user?.canAccessSongExport)
	})

	const scenesAreEditable = computed(() => {
		const currentDate = new Date()
		let cutoffDate = new Date(job.value?.fieldData?.date_start)
		// Get num days for cutoff from FM
		const numDays = job.value?.fieldData['jobs__PREFERENCES__cart::memberSiteEditScenesNumDaysBeforeJob']
		cutoffDate.setDate(cutoffDate.getDate() - numDays)
		const hasHadSongsSubmitted = Boolean(job.value?.fieldData?.hasHadSongsSubmitted ?? 0)

		// If songs have already been submitted do not allow edits
		if(hasHadSongsSubmitted) return false
		// If current date is after cutoff date do not allow edits
		if(currentDate > cutoffDate) return false
		return true
	})

	const sceneEditorOpen = ref<{open: boolean}>({open: false})

	function openSceneEditor() {
		sceneEditorOpen.value.open = true
	}

	function closeSceneEditor(command: string, notification?: Notification) {
		sceneEditorOpen.value.open = false

		// Reset store
		sceneEditorStore.sceneEditor.scenes = job.value?.portalData?.portal_main_scenes

		if(command === 'save') {
			displayNotification(notification)
			getData(true)
		}
	}
	// #endregion

	// #region Notification
	const showNotification = ref(false)
	const notificationIcon = ref(0)
	const notificationTitle = ref('')
	const notificationSubtitle = ref('')
	
	function displayNotification(notification: Notification) {
		notificationIcon.value = notification.icon
		notificationTitle.value = notification.title
		notificationSubtitle.value = notification.subtitle
		showNotification.value = true
	}
	// #endregion

</script>
<style scoped>
.modal {
  position: fixed; /* Stay in place */
  z-index: 100; /* Sit on top */
  left: 0;
  top: 0;
  width: 100%; /* Full width */
  height: 100%; /* Full height */
  overflow: auto; /* Enable scroll if needed */
  background-color: rgb(255,255,255); /* Fallback color */
  background-color: rgba(255,255,255,.8); /* Black w/ opacity */
}
</style>